import React from 'react';

export function BakingProcess() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Baking Process</h3>
        <p className="mb-4">
          The baking process is where all your preparation culminates in the transformation of dough into bread. Understanding different baking methods and techniques is crucial for achieving the perfect crust, crumb, and flavor in your sourdough bread.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Baking Methods Comparison</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Dutch Oven Method</h4>
            <p className="text-sm text-white/80 mt-1">
              Using a heavy cast iron pot with a lid to create a sealed environment for the bread.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Creates natural steam environment</li>
                  <li>Excellent heat retention and distribution</li>
                  <li>Produces consistent results</li>
                  <li>Great for home ovens</li>
                  <li>Minimal equipment needed</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Limitations:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Limited to round or oval shapes</li>
                  <li>Size constraints based on pot dimensions</li>
                  <li>Can be challenging to load dough</li>
                  <li>Requires careful handling of hot pot</li>
                </ul>
              </div>
            </div>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Process:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>Preheat dutch oven in 450-500°F (230-260°C) oven for 45-60 minutes</li>
                <li>Carefully transfer proofed dough into hot dutch oven</li>
                <li>Cover with lid and bake for 20-30 minutes (steam phase)</li>
                <li>Remove lid and continue baking for 15-25 minutes (crust development)</li>
                <li>Remove bread and cool on rack</li>
              </ol>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Open Bake with Steam</h4>
            <p className="text-sm text-white/80 mt-1">
              Baking directly on a stone or steel with added steam in the oven cavity.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Flexibility in bread shapes and sizes</li>
                  <li>Can bake multiple loaves simultaneously</li>
                  <li>Better control over crust development</li>
                  <li>Easier to monitor bread during baking</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Limitations:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Requires additional equipment</li>
                  <li>More challenging to create adequate steam</li>
                  <li>Results can vary based on oven quality</li>
                  <li>Steam can damage some home ovens</li>
                </ul>
              </div>
            </div>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Process:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>Preheat baking stone/steel at 450-500°F (230-260°C) for 45-60 minutes</li>
                <li>Prepare steam generation method (lava rocks, ice cubes, steam pan)</li>
                <li>Transfer dough to hot stone/steel using peel or parchment</li>
                <li>Add steam to oven immediately</li>
                <li>Bake with steam for 15-20 minutes</li>
                <li>Remove steam source and continue baking for 20-30 minutes</li>
                <li>Cool bread on rack</li>
              </ol>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Wood-Fired Oven</h4>
            <p className="text-sm text-white/80 mt-1">
              Traditional method using a wood-fired masonry oven.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Exceptional heat retention and distribution</li>
                  <li>Natural steam from bread itself</li>
                  <li>Unique flavor from wood smoke</li>
                  <li>Can bake many loaves at once</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Limitations:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Requires significant skill and experience</li>
                  <li>Temperature control is challenging</li>
                  <li>Long preheating time</li>
                  <li>Not practical for most home bakers</li>
                </ul>
              </div>
            </div>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Process:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>Heat oven to very high temperature (800-1000°F/427-538°C)</li>
                <li>Let temperature fall to baking range (450-550°F/232-288°C)</li>
                <li>Clear ash and embers from baking surface</li>
                <li>Load bread quickly to maintain heat</li>
                <li>Monitor closely as baking times are shorter</li>
                <li>Remove when well-browned and hollow-sounding</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Steam Generation Methods</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Importance of Steam</h4>
            <p className="text-sm text-white/80">
              Steam during the first phase of baking is crucial for:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Allowing maximum oven spring</li>
              <li>Creating a glossy crust</li>
              <li>Preventing premature crust formation</li>
              <li>Developing proper crust thickness</li>
            </ul>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Steam Pan Method</h4>
              <ul className="list-disc list-inside text-sm text-white/80">
                <li>Place heavy pan in bottom of oven during preheat</li>
                <li>Add 1 cup hot water when loading bread</li>
                <li>Remove pan after 15-20 minutes</li>
                <li>Caution: Can damage some ovens</li>
              </ul>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Ice Cube Method</h4>
              <ul className="list-disc list-inside text-sm text-white/80">
                <li>Place 3-4 ice cubes in preheated pan</li>
                <li>Gradual steam release as ice melts</li>
                <li>Less dramatic temperature drop</li>
                <li>Good for home ovens</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Temperature and Timing</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Temperature Guidelines</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Lean doughs: 450-500°F (230-260°C)</li>
                <li>Enriched doughs: 350-400°F (175-200°C)</li>
                <li>Whole grain: 425-450°F (218-230°C)</li>
                <li>Rye breads: 400-425°F (200-218°C)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Timing Guidelines</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Steam phase: 15-20 minutes</li>
                <li>Total time for 1kg loaf: 45-60 minutes</li>
                <li>Internal temperature: 205-210°F (96-99°C)</li>
                <li>Cool completely before cutting (1-2 hours)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Troubleshooting Common Baking Issues</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Pale Crust</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Increase oven temperature</li>
              <li>Ensure adequate steam during first phase</li>
              <li>Extend baking time</li>
              <li>Check oven calibration</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Burnt Bottom</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Double up baking sheets</li>
              <li>Use cornmeal or semolina as insulation</li>
              <li>Raise rack position slightly</li>
              <li>Reduce oven temperature</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Poor Oven Spring</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Check oven temperature with separate thermometer</li>
              <li>Ensure adequate steam</li>
              <li>Verify dough is not overproofed</li>
              <li>Score deeper or at better angle</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Uneven Baking</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Rotate bread halfway through baking</li>
              <li>Check for oven hot spots</li>
              <li>Ensure proper air circulation</li>
              <li>Use an oven stone or steel to even out heat</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}